<template>
    <div>
        <div v-if="showContactCreateForm">
            <create-contact-for-link-list
                @cancel-contact-creation="cancelContactCreation"
                @contact-created="selectNewlyCreatedContact">
            </create-contact-for-link-list>
        </div>

        <div v-show="!showContactCreateForm">
            <div class="my-5">
                <!--Select/Create Contact & Role-->
                <div>
                    <div class="grid grid-cols-8 gap-2">
                        <div class="col-span-8 md:col-span-5">
                            <remote-select
                                ref="selectContactInput"
                                :input-text-prop="inputTextProp"
                                v-model="selectedContact"
                                @focus="fetchContacts"
                                :remote-method="fetchContacts"
                                :loading="loadingContacts"
                                :data="contactResults"
                                item-label="full_name"
                                placeholder="Search contact to link">
                                <template slot-scope="props">
                                    <div class="flex items-center">
                                        <span>{{ props.item.full_name }}</span>
                                    </div>
                                </template>
                            </remote-select>
                        </div>
                        <span class="col-span-8 md:col-span-3 text-center block p-2 mb-3 rounded-md text-indigo-600 font-medium cursor-pointer hover:text-indigo-400"
                              @click.prevent="toggleCreatingContactSection(true)">
                            + Create New
                        </span>
                    </div>

                    <div v-if="selectedContact" class="pt-2">
                        <div class="grid grid-cols-2 gap-2">
                            <div class="col-span-2 md:col-span-1">
                                <label>Select Role
                                    <template v-if="selectedRole && showDatesFor.includes(selectedRole)"> and dates</template>
                                </label>
                                <div class="custom-radio-buttons-full">
                                    <el-radio-group :disabled="!selectedContact"
                                                    v-model="selectedRole"
                                                    size="small"
                                                    @change="clearDates">
                                        <el-radio-button v-for="role in roles" :label="role" :key="role" :value="role">{{ role | titlecase }}</el-radio-button>
                                    </el-radio-group>
                                </div>
                            </div>

                            <checkbox class="col-span-2 md:col-span-1 flex items-center pt-5 mt-0.5"
                                      v-model="isPrimaryContact"
                                      label="Set As Primary"
                                      label-styling="text-md">
                            </checkbox>
                        </div>
                        <div v-if="selectedRole && showDatesFor.includes(selectedRole)">
                            <!--Select Start & End Dates-->
                            <div class="form-group grid grid-cols-2 gap-2 mt-2 ">
                                <div class="col-span-2 md:col-span-1">
                                    <el-date-picker
                                        v-model="selectedStartDate"
                                        type="date"
                                        placeholder="From Date (Optional)"
                                        value-format="yyyy-MM-dd">
                                    </el-date-picker>
                                </div>
                                <div class="col-span-2 md:col-span-1">
                                    <el-date-picker
                                        v-model="selectedEndDate"
                                        type="date"
                                        placeholder="To Date (Optional)"
                                        value-format="yyyy-MM-dd">
                                    </el-date-picker>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!--Link Button-->
                <div v-if="selectedContact && selectedRole" class="mb-5 mt-2 pr-2">
                    <button
                        class="flex w-full items-center justify-center text-center block py-1 rounded-md border border-indigo-300 text-indigo-600 font-medium cursor-pointer hover:text-indigo-400"
                        @click.prevent="addSelectedContactToList">
                        + Add to list
                    </button>
                </div>
            </div>

            <div v-if="mutableContactLinks && mutableContactLinks.length > 0"
                 v-for="(linksInGroup, key) in contactLinksGroupedByRole"
                 class="bg-indigo-50 rounded mr-2 mt-2">
                <ul>
                    <li v-for="contactLink in linksInGroup" class="border-white border-t-4 rounded select-none">
                        <div class="p-2 grid grid-cols-12 gap-2 text-indigo-600 font-medium justify-between items-center">
                            <div class="col-span-5 flex">
                                <div>{{ contactLink.full_name }}</div>
                            </div>
                            <div class="col-span-5">
                                <span class="inline font-normal">{{ contactLink.role | titlecase }}</span>
                                <i v-if="contactLink.primary"
                                   class="ml-2 mdi mdi-check-decagram text-gray-600" data-toggle="tooltip"
                                   data-original-title="Primary Contact">
                                </i>
                                <div class="text-2xs text-gray-800">
                                    <span v-if="contactLink.start_date">
                                        <template v-if="!contactLink.end_date">starts on</template>
                                        {{ contactLink.start_date | date }}
                                    </span>
                                    <span v-if="contactLink.start_date && contactLink.end_date"> to </span>
                                    <span v-if="contactLink.end_date">
                                        <template v-if="!contactLink.start_date">ends on</template>
                                        {{ contactLink.end_date | date }}
                                    </span>
                                </div>
                            </div>

                            <button class="col-span-2 cursor-pointer flex justify-end"
                                    @click.prevent="removeContactFromList(contactLink.contact_id, contactLink.role)">
                                <icon-x-circle
                                    class="w-4 h-4 col-span-2 text-red-600">
                                </icon-x-circle>
                            </button>
                        </div>
                    </li>
                </ul>
            </div>

            <div v-if="!mutableContactLinks || mutableContactLinks.length === 0"
                 class="rounded px-6 py-10 flex flex-col items-center justify-center bg-gray-100 text-gray-400 font-medium">
                <div>
                    <icon-users class="w-10 h-10 my-3"></icon-users>
                </div>
                <span>No Contacts Linked</span>
            </div>
        </div>
    </div>
</template>

<script>
import PlusCircle from "../library/icons/PlusCircle";

export default {
    name: "ContactLinkList",
    components: {PlusCircle},
    props: {
        value: {
            default: () => [],
            type: Array
        },
        contactsUrl: {
            default: "/contacts/all",
            type: String,
            required: false
        },
        roles: {
            default: () => [],
            type: Array,
            required: true
        },
        showDatesFor: {
            default: () => [],
            type: Array,
            required: false,
            description: "Specifies the roles for which the dates (From/To) option will be available"
        },
        contactableType: {
            type: String,
            required: true,
            description: "Specifies the entity type to which the contacts will be/are linked. Example: (asset,inspection,ticket etc.)"
        },
        contactableId: {
            type: String,
            required: false,
            description: "Specifies the id of the entity to which the contacts will be/are linked. Can be null in the case of a new entity."
        }
    },

    data () {
        return {
            mutableContactLinks: this.value,
            selectedContact: null,
            selectedRole: null,
            selectedStartDate: null,
            selectedEndDate: null,
            isPrimaryContact: false,
            loadingContacts: false,
            contactResults: [],
            showContactCreateForm: false,
            inputTextProp: ""
        };
    },

    mounted () {
        this.mutableContactLinks = this.value;
    },

    watch: {
        value: {
            handler (newValue) {
                this.mutableContactLinks = newValue;
            },
            deep: true
        },

        mutableContactsLinks: {
            handler () {
                this.$emit("input", this.mutableContactLinks);
            },
            deep: true
        }
    },

    computed: {
        // Used to sort the different role types together. Makes it easier to
        // add spacing/format groups differently if we want to
        contactLinksGroupedByRole () {
            if (this.mutableContactLinks.length > 0) {
                return _.groupBy(this.mutableContactLinks, "role");
            }
        }
    },

    methods: {
        fetchContacts (query) {
            this.loadingContacts = true;

            axios.get(this.contactsUrl, {params: {filter_text: query}})
            .then((response) => {
                this.contactResults = response.data.data;
            }).catch((errors) => toastr.error("Could not fetch contacts!"));

            this.loadingContacts = false;
        },
        cancelContactCreation () {
            this.toggleCreatingContactSection(false);
        },
        toggleCreatingContactSection (bool) {
            this.showContactCreateForm = bool;
            Bus.$emit("creating-new-contact", bool);
        },
        selectNewlyCreatedContact (payload) {
            this.selectedContact = payload;
            this.toggleCreatingContactSection(false);
        },
        addSelectedContactToList () {
            let index = this.mutableContactLinks.findIndex(link => (link.contact_id === this.selectedContact.id && link.role === this.selectedRole));
            if (index === -1) {
                // If the user wants to add this new contact for the role as a primary contact
                // we need to reset any other primaries for the same role as there should only be
                // one primary per role type.
                if (this.isPrimaryContact) {
                    let self = this;
                    this.$set(this, "mutableContactLinks", this.mutableContactLinks.map(function (contact) {
                        if (contact.primary && contact.role === self.selectedRole) {
                            contact.primary = false;
                        }
                        return contact;
                    }));
                }

                this.mutableContactLinks.push({
                    start_date: this.selectedStartDate,
                    end_date: this.selectedEndDate,
                    contactable_type: this.contactableType,
                    contact_id: this.selectedContact.id,
                    role: this.selectedRole,
                    full_name: this.selectedContact.full_name,
                    primary: this.isPrimaryContact
                });
            }
            this.selectedContact = null;
            this.selectedRole = null;
            this.isPrimaryContact = false;
            this.clearDates();
            this.$refs.selectContactInput.clear();
            this.$emit("input", this.mutableContactLinks);

        },
        clearDates () {
            this.selectedStartDate = null;
            this.selectedEndDate = null;
        },
        removeContactFromList (contactId, role) {
            let index = this.value.findIndex(link => (link.contact_id === contactId && link.role === role));
            if (index !== -1) {
                this.mutableContactLinks = this.mutableContactLinks.splice(index, 1);
            }
        },
    }
};
</script>

<style scoped>

</style>
